// ABA "STATUS DE ENVIO" — o que o WhatsApp respondeu sobre cada fly de CAMPANHA.
//
// Pedido dele em 24/09/2026: *"criar uma aba Status de Envio. Nela seria exibido os status de envio
// do whatsapp. Cada status teria um sinal de + para expandir ao clicar, e visualizar os contatos"*.
// So' campanha — decisao dele; o fly que a vendedora manda pelo chat fica de fora.
//
// ⛔ **Nasce PLACAR, nao fila** (molde do `PainelNaoQuerReceber`): nada aqui se trabalha nem esvazia.
// Serve pra conferir quem ficou para tras.
//
// ⚠️ O "+" e' o MESMO botao da aba "Vendas hoje" do Caixa — ele ja' conhece a forma e o gesto.
//
// ⚠️ **Sem verde/vermelho** ([[user_daltonismo]]): os grupos se separam por ICONE e por TEXTO, e a
// unica cor que carrega sentido e' o azul do "leu", que e' a do proprio WhatsApp.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Ban, Check, CheckCheck, Minus, Plus, Trash2 } from 'lucide-react';
import { formatDataHoraSP, formatTelefoneBR } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { excluirContatoDeVez } from './excluir-contato';
import { Avatar } from '../../components/Avatar';
import { LightboxImagem } from '../atendimento/componentes/LightboxImagem';

interface Linha {
  fonte: 'mensagem' | 'fila';
  id: number;
  /** 'erro' (so' da fila) | 'sent' | 'delivered' | 'read' — o que o WhatsApp informou. */
  status: string;
  quando: string;
  campanha_id: number | null;
  campanha: string | null;
  /** `null` quando o contato ja foi apagado — o registro do envio sobrevive (FK `SET NULL`). */
  contato_id: number | null;
  /**
   * Acende a lixeira: numero recusado ("Nao saiu") ou contato cujas unicas mensagens sao flys que
   * nunca chegaram ("Saiu, nao chegou"). Quem decide e' o servidor, com as mesmas regras da trava.
   */
  pode_apagar: boolean;
  nome: string | null;
  foto_url: string | null;
  numero: string | null;
  motivo: string | null;
}

interface Grupo {
  id: string;
  titulo: string;
  explica: string;
  icone: ReactNode;
}

const CINZA = '#64748b';
// O azul do "lido" do proprio WhatsApp, um tom mais escuro pra ler sobre o painel claro.
const AZUL_LIDO = '#0284c7';

// A ORDEM e' a do caminho da mensagem: nem saiu → saiu → chegou → leu. Quem precisa de atencao
// fica em cima.
const GRUPOS: Grupo[] = [
  {
    id: 'erro',
    titulo: 'Nao saiu',
    explica: 'O WhatsApp recusou o numero — a mensagem nem saiu da loja.',
    icone: <Ban size={18} color={CINZA} />,
  },
  {
    id: 'sent',
    titulo: 'Saiu, nao chegou ao celular',
    explica: 'Um check cinza: a mensagem esta no WhatsApp mas nao chegou ao aparelho — celular desligado, sem internet, ou a pessoa bloqueou a loja. Parado assim por mais de um dia merece atencao.',
    icone: <Check size={18} color={CINZA} />,
  },
  {
    id: 'delivered',
    titulo: 'Chegou ao celular',
    explica: 'Dois checks cinza: entregue. A pessoa ainda nao abriu — ou desligou a confirmacao de leitura, e ai o azul nunca vem mesmo depois de ler.',
    icone: <CheckCheck size={18} color={CINZA} />,
  },
  {
    id: 'read',
    titulo: 'Leu',
    explica: 'Dois checks azuis: leitura confirmada.',
    icone: <CheckCheck size={18} color={AZUL_LIDO} />,
  },
];

// Status que o WhatsApp venha a mandar e que nao esta nos quatro acima nao some calado: cai aqui,
// e o grupo so' aparece quando tem alguem.
const GRUPO_OUTRO: Grupo = {
  id: 'outro',
  titulo: 'Outro status',
  explica: 'O WhatsApp devolveu um status que esta tela nao conhece.',
  icone: <Check size={18} color={CINZA} />,
};

export function PainelStatusEnvio({ sinalRecarga }: { sinalRecarga: number }) {
  const toast = useToast();
  const dialog = useDialog();
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [dias, setDias] = useState<number | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [abertos, setAbertos] = useState<Set<string>>(new Set());
  const [fotoAberta, setFotoAberta] = useState<{ url: string; nome: string } | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get<{ dias: number; linhas: Linha[] }>('/api/campanhas-fly/status-envio');
      // ⛔ Contato APAGADO sai da tela — pedido dele em 24/09/2026 (*"estou excluindo os contatos
      // invalidos, mas eles nao somem"*). A 1a versao deixava a linha como historico, escrita
      // "apagado"; ele apaga pra limpar, e a linha que fica desmente o gesto. O registro do envio
      // continua no banco (`campanha_fly_envios`, FK `SET NULL`) — so' nao e' mais mostrado.
      setLinhas((r.linhas ?? []).filter((l) => !(l.fonte === 'fila' && l.contato_id === null)));
      setDias(r.dias);
    } catch (e) {
      toast.erro((e as Error).message || 'Falha ao carregar o status de envio');
    } finally {
      setCarregando(false);
    }
  }, [toast]);

  // `sinalRecarga` e' o Atualizar do cabecalho da Gestao — o unico da tela ([DA-704]).
  useEffect(() => { void carregar(); }, [carregar, sinalRecarga]);

  // A MESMA peca da lixeira da aba Contatos e da campanha: previa do servidor, impedimentos em
  // palavras, confirmacao e so' entao o DELETE. Quem decide se pode e' o servidor.
  const apagar = async (l: Linha) => {
    if (!l.contato_id) return;
    if (await excluirContatoDeVez(l.contato_id, dialog, toast)) void carregar();
  };

  const alternar = (id: string) => {
    setAbertos((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
      return proximo;
    });
  };

  const conhecidos = new Set(GRUPOS.map((g) => g.id));
  const doGrupo = (id: string) =>
    id === 'outro' ? linhas.filter((l) => !conhecidos.has(l.status)) : linhas.filter((l) => l.status === id);
  const grupos = [...GRUPOS, ...(doGrupo('outro').length > 0 ? [GRUPO_OUTRO] : [])];

  return (
    <div style={{ padding: '4px 2px' }}>
      <div style={{ fontWeight: 600, marginBottom: 10 }}>
        {carregando && dias === null
          ? 'carregando...'
          : `${linhas.length} ${linhas.length === 1 ? 'fly de campanha' : 'flys de campanha'} nos ultimos ${dias} dias`}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {grupos.map((g) => {
          const itens = doGrupo(g.id);
          const aberto = abertos.has(g.id);
          const erro = g.id === 'erro';
          // Os dois grupos em que o contato pode sair de vez (pedidos dele, 24/09/2026): numero
          // recusado e fly que nunca chegou. Nos outros o cliente RECEBEU — nao ha o que limpar.
          const comLixeira = erro || g.id === 'sent';
          return (
            <div
              key={g.id}
              style={{
                borderRadius: 6, background: 'var(--panel-bg)',
                border: '1px solid var(--input-border, #d1d5db)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' }}>
                {/* O "+" da aba "Vendas hoje" do Caixa, copiado — mesma forma, mesmo gesto. Grupo
                    vazio nao abre: nao ha o que mostrar, e o botao apagado diz isso. */}
                <button
                  type="button"
                  onClick={() => alternar(g.id)}
                  disabled={itens.length === 0}
                  title={itens.length === 0 ? 'Ninguem neste status' : aberto ? 'Ocultar contatos' : 'Ver contatos'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 22, height: 22, borderRadius: 4, padding: 0, flex: '0 0 auto',
                    border: '1px solid var(--input-border, #d1d5db)',
                    background: aberto ? 'var(--accent, #2563eb)' : 'transparent',
                    color: aberto ? '#fff' : 'var(--accent, #2563eb)',
                    cursor: itens.length === 0 ? 'default' : 'pointer',
                    opacity: itens.length === 0 ? 0.35 : 1,
                  }}
                >
                  {aberto ? <Minus size={12} /> : <Plus size={12} />}
                </button>
                {g.icone}
                <span style={{ fontWeight: 600, fontSize: 15 }}>
                  {g.titulo} <span style={{ fontVariantNumeric: 'tabular-nums' }}>({itens.length})</span>
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{g.explica}</span>
              </div>

              {aberto && itens.length > 0 && (
                <div style={{ overflowX: 'auto', padding: '0 12px 10px' }}>
                  <table className="tabela-grid" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Contato</th>
                        <th style={{ textAlign: 'left' }}>Telefone</th>
                        <th style={{ textAlign: 'left' }}>Campanha</th>
                        <th style={{ textAlign: 'left' }}>{erro ? 'Tentou em' : 'Enviado em'}</th>
                        {erro && <th style={{ textAlign: 'left' }}>Motivo</th>}
                        {comLixeira && <th />}
                      </tr>
                    </thead>
                    <tbody>
                      {itens.map((l) => (
                        <tr key={`${l.fonte}-${l.id}`}>
                          <td>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Avatar
                                nome={l.nome || '?'}
                                fotoUrl={l.foto_url}
                                tamanho={26}
                                onFotoClick={l.foto_url
                                  ? () => setFotoAberta({ url: l.foto_url!, nome: l.nome || formatTelefoneBR(l.numero) })
                                  : undefined}
                              />
                              {l.nome || <span style={{ color: 'var(--text-muted)' }}>sem nome</span>}
                            </span>
                          </td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatTelefoneBR(l.numero)}</td>
                          <td>{l.campanha || '—'}</td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatDataHoraSP(l.quando)}</td>
                          {erro && <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{l.motivo || '—'}</td>}
                          {/* Lixeira so' quando o servidor diz `pode_apagar` — erro de arte ou de rede
                              nao diz nada do numero, e contato com qualquer outra mensagem alem do
                              fly que nao chegou continua preso. Contato apagado nem chega aqui
                              (filtrado no carregar). */}
                          {comLixeira && (
                            <td style={{ textAlign: 'right', width: 44 }}>
                              {l.contato_id && l.pode_apagar ? (
                                <button
                                  type="button"
                                  onClick={() => void apagar(l)}
                                  className="btn-icon"
                                  style={{ color: '#dc2626' }}
                                  title={erro
                                    ? 'Apagar este contato de vez (o WhatsApp recusou o número)'
                                    : 'Apagar este contato de vez (o fly nunca chegou ao celular)'}
                                  aria-label={`Apagar o contato ${l.nome || formatTelefoneBR(l.numero)}`}
                                >
                                  <Trash2 size={17} />
                                </button>
                              ) : null}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {fotoAberta && (
        <LightboxImagem
          imagens={[{ url: fotoAberta.url, legenda: fotoAberta.nome }]}
          onFechar={() => setFotoAberta(null)}
        />
      )}
    </div>
  );
}
